(() => {
  if (customElements.get('hone-catalog-360')) return;
  let modulePromise;
  const loadViewer = (src) => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!modulePromise) {
      modulePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = src;
        script.onload = () => customElements.get('model-viewer') ? resolve() : reject(new Error('Viewer unavailable'));
        script.onerror = () => reject(new Error('Viewer unavailable'));
        document.head.appendChild(script);
      }).catch((error) => { modulePromise = null; throw error; });
    }
    return modulePromise;
  };
  class HoneCatalog360 extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-catalog-config]').textContent);
      this.poster = this.querySelector('[data-poster]');
      this.viewport = this.querySelector('[data-viewport]');
      this.host = this.querySelector('[data-model-host]');
      this.status = this.querySelector('[data-status]');
      this.productHeading = this.querySelector('[data-product-name]');
      this.viewButtons = this.querySelector('[data-views]');
      this.picker = this.querySelector('[data-product-picker]');
      this.activateButton = this.querySelector('[data-action="toggle"]');
      this.activateLabel = this.querySelector('[data-activate-label]');
      this.tools = this.querySelector('[data-tools]');
      this.token = 0;
      this.onClick = (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.dataset.product) this.selectProduct(button.dataset.product);
        else if (button.dataset.view) this.selectView(button.dataset.view);
        else if (button.dataset.action === 'toggle') this.requested ? this.stopModel() : this.activate();
        else if (button.dataset.action === 'reset') this.reset();
        else if (button.dataset.action === 'flip-forward') this.rotateModel(1, 30);
        else if (button.dataset.action === 'flip-backward') this.rotateModel(1, -30);
        else if (button.dataset.action === 'roll-left') this.rotateModel(0, 30);
        else if (button.dataset.action === 'roll-right') this.rotateModel(0, -30);
        else if (button.dataset.action === 'in') this.zoom(0.8);
        else if (button.dataset.action === 'out') this.zoom(1.25);
      };
      this.onChange = () => this.selectProduct(this.picker.value);
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.requested) {
          this.stopModel();
          this.activateButton.focus();
          event.preventDefault();
          return;
        }
        if (!this.model || !this.classList.contains('is-active') || event.target.tagName !== 'MODEL-VIEWER') return;
        const orbit = this.model.getCameraOrbit();
        const step = Math.PI / 18;
        const changes = {ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step]};
        if (changes[event.key]) {
          const [theta, phi] = changes[event.key];
          this.model.cameraOrbit = (orbit.theta + theta) + 'rad ' + Math.max(0, Math.min(Math.PI, orbit.phi + phi)) + 'rad ' + orbit.radius + 'm';
        } else if (event.key === '+' || event.key === '=') this.zoom(0.8);
        else if (event.key === '-') this.zoom(1.25);
        else if (event.key === 'Home') this.reset();
        else return;
        event.preventDefault();
        event.stopPropagation();
      };
      this.addEventListener('click', this.onClick);
      this.picker.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey, true);
      this.selectProduct(this.config.products[0].key);
    }
    disconnectedCallback() {
      this.stopModel();
      this.removeEventListener('click', this.onClick);
      this.picker.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey, true);
      this.initialized = false;
    }
    selectProduct(key) {
      const product = this.config.products.find((item) => item.key === key);
      if (!product) return;
      this.stopModel();
      this.product = product;
      this.productHeading.textContent = product.name;
      this.picker.value = product.key;
      this.querySelectorAll('[data-product]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.product === key)));
      this.viewButtons.replaceChildren();
      this.viewButtons.hidden = product.views.length < 2;
      product.views.forEach((view) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.view = view.key;
        button.textContent = view.label;
        button.setAttribute('aria-pressed', 'false');
        this.viewButtons.appendChild(button);
      });
      this.selectView(product.views[0].key);
    }
    selectView(key) {
      const view = this.product.views.find((item) => item.key === key);
      if (!view) return;
      const resume = this.requested;
      this.stopModel();
      this.view = view;
      if (this.poster.getAttribute('src') !== view.poster) this.poster.src = view.poster;
      this.poster.alt = this.product.name + ' - ' + view.label;
      this.viewButtons.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === key)));
      if (resume) this.activate();
    }
    stopModel() {
      this.token = (this.token || 0) + 1;
      this.requested = false;
      clearTimeout(this.timeout);
      if (this.model) { this.model.removeAttribute('src'); this.model.remove(); }
      this.model = null;
      this.turn = null;
      if (!this.host) return;
      this.host.replaceChildren();
      this.classList.remove('is-active', 'is-loading');
      this.viewport.setAttribute('aria-busy', 'false');
      this.tools.hidden = true;
      this.activateLabel.textContent = this.config.labels.explore;
      this.status.textContent = '';
    }
    async activate() {
      this.stopModel();
      const token = this.token;
      this.requested = true;
      this.classList.add('is-loading');
      this.viewport.setAttribute('aria-busy', 'true');
      this.activateLabel.textContent = this.config.labels.preview;
      this.status.textContent = this.config.labels.loading;
      this.timeout = setTimeout(() => this.fail(token), 120000);
      try {
        await loadViewer(this.dataset.module);
        if (token !== this.token || !this.isConnected) return;
        const model = document.createElement('model-viewer');
        this.model = model;
        model.setAttribute('alt', this.product.name + ' - ' + this.view.label);
        model.setAttribute('aria-label', this.product.name + '. ' + this.config.labels.ready);
        model.setAttribute('camera-controls', '');
        model.setAttribute('disable-pan', '');
        model.setAttribute('touch-action', 'none');
        model.setAttribute('interaction-prompt', 'none');
        model.setAttribute('environment-image', this.view.environment || 'neutral');
        model.setAttribute('shadow-intensity', String(this.view.shadowIntensity ?? 0.75));
        model.setAttribute('shadow-softness', String(this.view.shadowSoftness ?? 0.9));
        model.setAttribute('exposure', String(this.view.exposure ?? 1));
        model.setAttribute('field-of-view', '30deg');
        model.setAttribute('min-camera-orbit', 'auto 0deg 25%');
        model.setAttribute('max-camera-orbit', 'auto 180deg 300%');
        model.setAttribute('camera-orbit', this.view.orbit);
        model.setAttribute('camera-target', this.view.target);
        model.setAttribute('orientation', this.view.orientation);
        model.setAttribute('loading', 'eager');
        model.setAttribute('reveal', 'auto');
        model.setAttribute('tabindex', '0');
        model.addEventListener('progress', (event) => {
          if (token !== this.token || !this.classList.contains('is-loading')) return;
          const progress = Math.round((event.detail.totalProgress || 0) * 100);
          this.status.textContent = this.config.labels.loading + (progress ? ' ' + progress + '%' : '');
        });
        model.addEventListener('load', () => {
          if (token !== this.token) return;
          if (this.view.discFaceBaseColor) {
            const discFaces = new Set(['Light silver diamond surface with authentic source grain', 'Light silver diamond surface with authentic source grain.001']);
            model.model.materials.forEach((material) => {
              if (discFaces.has(material.name)) material.pbrMetallicRoughness.setBaseColorFactor(this.view.discFaceBaseColor);
            });
          }
          clearTimeout(this.timeout);
          this.classList.remove('is-loading');
          this.classList.add('is-active');
          this.viewport.setAttribute('aria-busy', 'false');
          this.tools.hidden = false;
          this.status.textContent = this.config.labels.ready;
          this.initialRadius = model.getCameraOrbit().radius;
          model.jumpCameraToGoal();
        }, {once: true});
        model.addEventListener('error', () => this.fail(token), {once: true});
        this.host.appendChild(model);
        model.src = this.view.model;
      } catch (error) {
        this.fail(token);
      }
    }
    fail(token) {
      if (token !== this.token) return;
      this.stopModel();
      this.status.textContent = this.config.labels.error;
    }
    async rotateModel(axis, degrees) {
      const model = this.model;
      if (!model || !this.classList.contains('is-active')) return;
      if (window.HoneFreeRotation) { window.HoneFreeRotation.rotate(model, 0, axis === 1 ? degrees : 0, axis === 0 ? degrees : 0); return; }
      if (!this.turn) this.turn = this.view.orientation.split(/\s+/).map((value) => parseFloat(value) || 0);
      this.turn[axis] = (this.turn[axis] + degrees) % 360;
      model.orientation = this.turn.map((value) => value + 'deg').join(' ');
      model.cameraTarget = 'auto auto auto';
      await model.updateComplete;
      if (this.model === model) model.jumpCameraToGoal();
    }
    reset() {
      if (!this.model) return;
      this.turn = null;
      this.model.orientation = this.view.orientation;
      this.model.cameraTarget = this.view.target;
      this.model.cameraOrbit = this.view.orbit;
      this.model.fieldOfView = '30deg';
      this.model.updateComplete.then(() => { if (this.model) this.model.jumpCameraToGoal(); });
    }
    zoom(factor) {
      if (!this.model || !this.classList.contains('is-active')) return;
      const orbit = this.model.getCameraOrbit();
      const radius = Math.max(this.initialRadius * 0.3, Math.min(this.initialRadius * 2.5, orbit.radius * factor));
      this.model.cameraOrbit = orbit.theta + 'rad ' + orbit.phi + 'rad ' + radius + 'm';
    }
  }
  customElements.define('hone-catalog-360', HoneCatalog360);
})();
